/**
 * ApplicantChatPanel — small two-way message thread for the applicant/admin.
 * Reuses the same visual on both sides; `role` decides which endpoints to
 * hit. Kept compact — designed to nest inside a card or drawer row.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Send, Loader2, MessageCircle, Paperclip, X, FileText, Image as ImageIcon, Download, Sparkles } from "lucide-react";
import api, { formatApiError, backendUrl } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const ATTACH_ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp";
const ATTACH_MAX_MB = 8;
const ATTACH_MAX_BYTES = ATTACH_MAX_MB * 1024 * 1024;

const relTime = (iso) => {
    if (!iso) return "";
    try {
        const diff = Math.max(0, Date.now() - new Date(iso).getTime());
        const m = Math.round(diff / 60_000);
        if (m < 1) return "just now";
        if (m < 60) return `${m}m ago`;
        const h = Math.round(m / 60);
        if (h < 24) return `${h}h ago`;
        const d = Math.round(h / 24);
        if (d < 30) return `${d}d ago`;
        return new Date(iso).toLocaleDateString();
    } catch { return ""; }
};

export const ApplicantChatPanel = ({ applicationId, role, testIdPrefix = "chat", onCountChange }) => {
    // role: 'applicant' → /professional/applications/{id}/messages
    //       'admin'     → /admin/applications/{id}/messages
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState("");
    const [sending, setSending] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const [drafting, setDrafting] = useState(false);
    const scrollerRef = useRef(null);
    const fileRef = useRef(null);

    const endpoint = role === "admin"
        ? `/admin/applications/${applicationId}/messages`
        : `/professional/applications/${applicationId}/messages`;
    const attachmentEndpoint = `${endpoint}/with-attachment`;

    const authHeader = () => {
        if (role === "admin") {
            const t = localStorage.getItem("simonara_admin_token") || "";
            return t ? { Authorization: `Bearer ${t}` } : {};
        }
        const t = localStorage.getItem("simonara_user_token") || "";
        return t ? { "X-User-Token": t } : {};
    };

    const buildAttachmentUrl = (msgId) =>
        backendUrl(`/api${endpoint}/${msgId}/attachment`);

    const openAttachment = async (msgId, filename) => {
        try {
            const r = await fetch(buildAttachmentUrl(msgId), { headers: authHeader() });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const blob = await r.blob();
            const url = URL.createObjectURL(blob);
            window.open(url, "_blank", "noopener,noreferrer");
            setTimeout(() => URL.revokeObjectURL(url), 60_000);
        } catch {
            toast.error(`Couldn't open ${filename || "attachment"}.`);
        }
    };

    const load = useCallback(async () => {
        if (!applicationId) return;
        try {
            setLoading(true);
            const { data } = await api.get(endpoint);
            const list = data?.messages || [];
            setMessages(list);
            if (typeof onCountChange === "function") onCountChange(list.length);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [applicationId, endpoint, onCountChange]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        // Auto-scroll to the latest message when the list grows.
        if (scrollerRef.current) {
            scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
        }
    }, [messages.length]);

    const onPickFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > ATTACH_MAX_BYTES) {
            toast.error(`File too large. Max ${ATTACH_MAX_MB} MB.`);
            e.target.value = "";
            return;
        }
        const name = (f.name || "").toLowerCase();
        const ok = /\.(pdf|png|jpe?g|webp)$/.test(name) ||
            ["application/pdf", "image/png", "image/jpeg", "image/webp"].includes(f.type || "");
        if (!ok) {
            toast.error("Only PDF, PNG, JPG, or WEBP files are supported.");
            e.target.value = "";
            return;
        }
        setPendingFile(f);
    };

    const clearPending = () => {
        setPendingFile(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const draftReply = async () => {
        // Admin-only. Calls Claude via the backend and pipes the response
        // into the composer so the admin can edit before sending.
        setDrafting(true);
        try {
            const { data } = await api.post(`${endpoint}/draft`);
            setDraft(data?.draft || "");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setDrafting(false);
        }
    };

    const send = async (e) => {
        e?.preventDefault();
        const text = draft.trim();
        if (!text && !pendingFile) return;
        setSending(true);
        try {
            let msg;
            if (pendingFile) {
                const fd = new FormData();
                fd.append("message", text);
                fd.append("file", pendingFile);
                const { data } = await api.post(attachmentEndpoint, fd);
                msg = data;
            } else {
                const { data } = await api.post(endpoint, { message: text });
                msg = data;
            }
            setMessages((prev) => [...prev, msg]);
            setDraft("");
            clearPending();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="rounded-md border border-border bg-card" data-testid={`${testIdPrefix}-panel`}>
            <div className="px-3 py-2 border-b border-border flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                <MessageCircle size={10} /> Private message thread
                <span className="text-muted-foreground/70 normal-case tabular-nums ml-1">
                    · {messages.length} message{messages.length === 1 ? "" : "s"}
                </span>
            </div>
            <div
                ref={scrollerRef}
                className="max-h-64 overflow-y-auto p-3 space-y-2"
                data-testid={`${testIdPrefix}-thread`}
            >
                {loading ? (
                    <div className="flex justify-center py-4">
                        <Loader2 size={14} className="animate-spin text-muted-foreground" />
                    </div>
                ) : messages.length === 0 ? (
                    <p className="text-[11px] text-muted-foreground italic text-center py-4">
                        {role === "admin"
                            ? "No messages yet. Break the ice — the applicant will get an email when you reply."
                            : "No messages yet. Say hi to the hiring team — they'll get an email the moment you send."}
                    </p>
                ) : (
                    messages.map((m) => {
                        const mine = m.from_kind === role;
                        const att = m.attachment;
                        return (
                            <div
                                key={m.id}
                                data-testid={`${testIdPrefix}-msg-${m.id}`}
                                className={`flex ${mine ? "justify-end" : "justify-start"}`}
                            >
                                <div className={`max-w-[80%] rounded-lg px-3 py-2 ${
                                    mine
                                        ? "bg-emerald-500/15 text-foreground"
                                        : "bg-muted text-foreground"
                                }`}>
                                    {m.message && (
                                        <p className="text-xs whitespace-pre-wrap">{m.message}</p>
                                    )}
                                    {att && (
                                        <button
                                            type="button"
                                            onClick={() => openAttachment(m.id, att.filename)}
                                            data-testid={`${testIdPrefix}-attachment-${m.id}`}
                                            className={`${m.message ? "mt-2" : ""} inline-flex items-center gap-2 text-[11px] rounded-md border border-border bg-background hover:bg-emerald-500/5 transition px-2 py-1.5 w-full text-left`}
                                        >
                                            {att.kind === "image" ? (
                                                <ImageIcon size={12} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                                            ) : (
                                                <FileText size={12} className="text-emerald-600 dark:text-emerald-400 shrink-0" />
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate font-medium text-foreground">{att.filename}</div>
                                                <div className="text-[10px] text-muted-foreground">
                                                    {att.kind === "image" ? "Image" : "PDF"} · {Math.max(1, Math.round((att.size_bytes || 0) / 1024))} KB
                                                </div>
                                            </div>
                                            <Download size={12} className="text-muted-foreground shrink-0" />
                                        </button>
                                    )}
                                    <div className="mt-1 text-[9px] text-muted-foreground">
                                        {mine ? "You" : (m.from_name || "Hiring team")} · {relTime(m.at)}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
            <form onSubmit={send} className="p-2 border-t border-border">
                {pendingFile && (
                    <div className="mb-2 flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/5 px-2 py-1.5" data-testid={`${testIdPrefix}-pending`}>
                        <Paperclip size={12} className="text-emerald-600" />
                        <div className="min-w-0 flex-1">
                            <div className="text-[11px] font-medium truncate text-foreground">{pendingFile.name}</div>
                            <div className="text-[10px] text-muted-foreground">
                                {Math.round(pendingFile.size / 1024)} KB
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={clearPending}
                            className="text-muted-foreground hover:text-foreground p-1 rounded"
                            data-testid={`${testIdPrefix}-pending-clear`}
                            aria-label="Remove pending attachment"
                        ><X size={11} /></button>
                    </div>
                )}
                <div className="flex items-start gap-2">
                    <textarea
                        rows={2}
                        value={draft}
                        onChange={(e) => setDraft(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(e);
                        }}
                        placeholder={role === "admin" ? "Reply to the applicant…" : "Message the hiring team…"}
                        className="flex-1 text-xs px-2 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-emerald-500 resize-none"
                        data-testid={`${testIdPrefix}-input`}
                    />
                    <label
                        htmlFor={`${testIdPrefix}-file`}
                        className="cursor-pointer text-muted-foreground hover:text-emerald-600 dark:hover:text-emerald-400 rounded border border-border px-2 py-1.5"
                        title="Attach a PDF or image (max 8 MB)"
                        data-testid={`${testIdPrefix}-attach`}
                    >
                        <Paperclip size={13} />
                    </label>
                    {role === "admin" && (
                        <button
                            type="button"
                            onClick={draftReply}
                            disabled={drafting || sending}
                            className="cursor-pointer text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-60 rounded border border-emerald-500/40 bg-emerald-500/5 px-2 py-1.5 inline-flex items-center gap-1"
                            title="Ask Claude to draft a warm reply based on the last few messages"
                            data-testid={`${testIdPrefix}-draft`}
                        >
                            {drafting ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                            <span className="text-[10px] font-semibold">AI</span>
                        </button>
                    )}
                    <input
                        id={`${testIdPrefix}-file`}
                        ref={fileRef}
                        type="file"
                        accept={ATTACH_ACCEPT}
                        onChange={onPickFile}
                        className="hidden"
                        data-testid={`${testIdPrefix}-file-input`}
                    />
                    <button
                        type="submit"
                        disabled={sending || (!draft.trim() && !pendingFile)}
                        className="btn-green text-[11px] px-2.5 py-1.5 inline-flex items-center gap-1 disabled:opacity-60"
                        data-testid={`${testIdPrefix}-send`}
                    >
                        {sending ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                        Send
                    </button>
                </div>
            </form>
        </div>
    );
};

export default ApplicantChatPanel;
